import ky from 'ky';

import {getOptions, toQueryString} from '~api/api';

const AUTH_API = import.meta.env.VITE_AUTH_API ?? '';
const autoMLEndpoint = `${AUTH_API}/automl`;

/**
 * Sanitises JSON text that may contain non-finite numeric literals
 * (`Infinity`, `-Infinity`, `NaN`) which are invalid in standard JSON.
 * Replaces them with their string equivalents before parsing.
 */
const sanitizeNonFiniteJson = (text: string): unknown => {
  let t = text.replace(/([:\\[,]\s*)-Infinity\b/g, '$1"-inf"');
  t = t.replace(/([:\\[,]\s*)Infinity\b/g, '$1"inf"');
  t = t.replace(/([:\\[,]\s*)NaN\b/g, '$1"nan"');
  return JSON.parse(t);
};

/**
 * @throws {HTTPError} @see with content {@link ApiErrorResponse}
 */
export const postStartModelTraining = ({
  projectId,
  modelId,
  emailNotification,
  dryRun = false,
  withHyparam = false,
}: AutoMlModelArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/models/${modelId}/v2/train`,
      getOptions({
        signalId: 'postStartModelTraining',
        data: {
          dryRun,
          emailNotification,
          withHyparam,
        },
      }),
    )
    .json<AutoMLTrainingGroupResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestModelPipeline = async ({projectId, modelId}: AutoMlModelArgs) => {
  const response = await ky.get(`${autoMLEndpoint}/${projectId}/models/${modelId}/pipelines/v2/latest`, {
    ...getOptions({signalId: 'getLatestModelPipeline'}),
    parseJson: sanitizeNonFiniteJson,
  });
  if (response.status === 204) {
    return null;
  }

  return response.json() as Promise<AutoMLTrainingGroupResponse>;
};

/**
 * Fetches the latest fully-completed training group for a model.
 * Returns null when no completed training exists (HTTP 204).
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestSuccessfulModelPipeline = async ({projectId, modelId}: AutoMlModelArgs) => {
  const response = await ky.get(`${autoMLEndpoint}/${projectId}/models/${modelId}/pipelines/v2/latest_successful`, {
    ...getOptions({signalId: 'getLatestSuccessfulModelPipeline'}),
    parseJson: sanitizeNonFiniteJson,
  });
  if (response.status === 204) {
    return null;
  }

  return response.json() as Promise<AutoMLTrainingGroupResponse>;
};

/**
 * Get the latest training groups for all models in the project.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestTrainingGroups = async ({projectId}: {projectId: string}) => {
  const response = await ky.get(`${autoMLEndpoint}/${projectId}/training_groups/latest`, {
    ...getOptions({signalId: 'getLatestTrainingGroups'}),
    parseJson: sanitizeNonFiniteJson,
  });

  return response.json() as Promise<AutoMLTrainingGroupsResponse>;
};

/**
 * Get the latest successful pipeline for all models in the project.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestSuccessfulTrainingGroups = async ({projectId}: {projectId: string}) => {
  const response = await ky.get(`${autoMLEndpoint}/${projectId}/training_groups/latest_successful`, {
    ...getOptions({signalId: 'getLatestSuccessfulTrainingGroups'}),
    parseJson: sanitizeNonFiniteJson,
  });

  return response.json() as Promise<AutoMLTrainingGroupsResponse>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getAllModelPipelines = ({projectId, modelId}: AutoMlModelArgs) => {
  return ky
    .get(`${autoMLEndpoint}/${projectId}/models/${modelId}/pipelines`, {
      ...getOptions({signalId: 'getAllModelPipelines'}),
      parseJson: sanitizeNonFiniteJson,
    })
    .json<AutoMLPipelineResponse[]>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postCancelPipeline = ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  return ky
    .post(`${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/cancel`, getOptions({signalId: 'postCancelPipeline'}))
    .json<AutoMLPipelineStatusResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getDatasetFinetuneData = async ({
  projectId,
  pipelineId,
  datasetId,
}: Omit<AutoMLDatasetFinetuneDataArgs, 'interpretation'>) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/finetune/v1/${datasetId}`,
    getOptions({signalId: 'getDatasetFinetuneData'}),
  );

  return response.json() as Promise<FinetuneDataResponse>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getDatasetFinetuneDataV2 = async ({
  projectId,
  pipelineId,
  datasetId,
  interpretation,
}: AutoMLDatasetFinetuneDataArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/finetune/v2/dataset/${datasetId}/interpretation/${interpretation}`,
    getOptions({signalId: 'getDatasetFinetuneData'}),
  );

  return response.json() as Promise<FinetuneDataResponse>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getKeypointFinetuneData = async ({projectId, pipelineId}: AutoMLKeypointDatasetFinetuneDataArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/keypoint_finetune`,
    getOptions({signalId: 'getKeypointFinetuneData'}),
  );

  return response.json() as Promise<KeypointFinetuneData>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getKeypointFinetuneDataByDataset = async ({
  projectId,
  pipelineId,
  datasetId,
}: AutoMLKeypointDatasetFinetuneDataArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/keypoint_finetune/dataset/${datasetId}`,
    getOptions({signalId: 'getKeypointFinetuneDataByDataset'}),
  );

  return response.json() as Promise<KeypointFinetuneData>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getPublishingPipelineStatus = async ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/publish/${pipelineId}/status`,
    getOptions({signalId: 'getPublishingPipelineStatus'}),
  );

  return response.json() as Promise<AutoMLPipelineStatusResponse>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postPublishModel = ({
  projectId,
  pipelineId,
  confidenceThreshold,
  areaSizeThreshold,
}: AutoMLPublishModelArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/v2/publish`,
      getOptions({
        signalId: 'postPublishModel',
        data: {
          confidenceThreshold,
          areaSizeThreshold,
        },
      }),
    )
    .json<AutoMLPublishPipelineResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postSaveFinetuneThresholds = ({
  projectId,
  pipelineId,
  confidenceThreshold,
  areaSizeThreshold,
}: AutoMLPublishModelArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/finetune/save`,
      getOptions({
        signalId: 'postSaveFinetuneThresholds',
        data: {
          confidenceThreshold,
          areaSizeThreshold,
        },
      }),
    )
    .json<null>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postCancelPublishingModel = ({
  projectId,
  pipelineId,
}: Pick<AutoMLPublishModelArgs, 'pipelineId' | 'projectId'>) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/pipelines/publish/${pipelineId}/cancel`,
      getOptions({
        signalId: 'postCancelPublishingModel',
      }),
    )
    .json<AutoMLPipelineStatusResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postRecomputeFinetune = ({projectId, pipelineId, datasetId}: RecomputeFinetuneArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/finetune/v1/${datasetId}/compute`,
      getOptions({
        signalId: 'postRecomputeFinetune',
      }),
    )
    .json<FinetuneStatusResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postTriggerEvaluationPipeline = ({
  projectId,
  pipelineId,
  imageIds,
  enhancedTesting,
}: AutoMLTriggerEvaluationPipelineArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/evaluate`,
      getOptions({
        signalId: 'postTriggerEvaluationPipeline',
        data: {
          imageIds,
          // Only send the flag when enabled: the normal test-model request stays byte-identical to the
          // pre-enhanced-testing contract, so it cannot break against a backend without the new field.
          ...(enhancedTesting && {enhancedTesting}),
        },
      }),
    )
    .json<AutoMLTriggerEvaluationPipelineResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getEvaluationPipeline = async ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/evaluations/${pipelineId}`,
    getOptions({signalId: 'getEvaluationPipeline'}),
  );

  return response.json() as Promise<AutoMLEvaluationPipelineResponse>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestSavedFinetune = async ({projectId, modelId}: AutoMlModelArgs) => {
  try {
    const response = await ky.get(
      `${autoMLEndpoint}/${projectId}/pipelines/models_finetune/${modelId}/latest`,
      getOptions({signalId: 'getLatestSavedFinetune'}),
    );
    return (await response.json()) as FinetuneSavedItem;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestEvaluationPipelineByTrainingPipeline = async ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/evaluations/training_pipeline/${pipelineId}/latest`,
    getOptions({signalId: 'getLatestEvaluationPipelineByTrainingPipeline'}),
  );

  return response.json() as Promise<AutoMLEvaluationPipelineResponse>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getEvaluationPipelines = async ({projectId}: {projectId: string}) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/evaluations`,
    getOptions({signalId: 'getEvaluationPipeline'}),
  );

  return response.json() as Promise<AutoMLEvaluationPipelineResponse[]>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getEvaluationPipelinesByModel = async ({projectId, modelId}: {projectId: string; modelId: string}) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/${modelId}/evaluations`,
    getOptions({signalId: 'getEvaluationPipelinesByModel'}),
  );

  return response.json() as Promise<AutoMLEvaluationPipelineResponse[]>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getEvaluationPipelineResults = async ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/evaluations/${pipelineId}/results`,
    getOptions({signalId: 'getEvaluationPipelineResults'}),
  );

  return response.json() as Promise<AutoMLEvaluationPipelineResultsResponse>;
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postCancelEvaluation = ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/pipelines/evaluations/${pipelineId}/cancel`,
      getOptions({
        signalId: 'postCancelEvaluation',
      }),
    )
    .json<AutoMLCancelEvaluationPipelineResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postStopTraining = ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/stop_training`,
      getOptions({
        signalId: 'postStopTraining',
      }),
    )
    .json<AutoMLPipelineResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestPublishingPipeline = async ({projectId}: {projectId: string}) => {
  return await ky
    .get(
      `${autoMLEndpoint}/${projectId}/pipelines/publish/latest`,
      getOptions({signalId: 'getLatestPublishingPipeline'}),
    )
    .json<AutoMLPublishPipelineResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getFinetuneStatus = async ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/finetune/status`,
    getOptions({signalId: 'getFinetuneStatus'}),
  );

  return response.json() as Promise<FinetuneStatusOverviewResponse>;
};

export const getTrainingPipelineLogs = async ({projectId, pipelineId}: {projectId: string; pipelineId: string}) => {
  const options = getOptions({signalId: `getTrainingPipelineLogs-${pipelineId}`});
  // We return the raw Response to allow callers to access headers (e.g., filename)
  return ky.get(`${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/logs`, options);
};

/**
 * Trigger an image selection curation pipeline for a model.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postTriggerImageSelectionPipeline = ({projectId, modelId}: AutoMlModelArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/image_selection/${modelId}/trigger`,
      getOptions({signalId: 'postTriggerImageSelectionPipeline'}),
    )
    .json<TriggerImageCurationPipelineResponse>();
};

/**
 * Trigger an image cleanup curation pipeline for a model.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postTriggerImageCleanupPipeline = ({projectId, modelId}: AutoMlModelArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/image_cleanup/${modelId}/trigger`,
      getOptions({signalId: 'postTriggerImageCleanupPipeline'}),
    )
    .json<TriggerImageCurationPipelineResponse>();
};

/**
 * Get the latest image selection curation pipeline for a model.
 * Returns null if none exists (204 response).
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestImageSelectionPipeline = async ({projectId, modelId}: AutoMlModelArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/image_curation_pipelines/model/${modelId}/latest/image_selection`,
    getOptions({signalId: 'getLatestImageSelectionPipeline'}),
  );
  if (response.status === 204) {
    return null;
  }
  return response.json() as Promise<GetImageCurationPipelineResponse>;
};

/**
 * Get the latest image cleanup curation pipeline for a model.
 * Returns null if none exists (204 response).
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestImageCleanupPipeline = async ({projectId, modelId}: AutoMlModelArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/image_curation_pipelines/model/${modelId}/latest/image_cleanup`,
    getOptions({signalId: 'getLatestImageCleanupPipeline'}),
  );
  if (response.status === 204) {
    return null;
  }
  return response.json() as Promise<GetImageCurationPipelineResponse>;
};

/**
 * Trigger a synthetic defect generation pipeline for a model.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postTriggerSyntheticDefectPipeline = ({
  projectId,
  modelId,
  trainingPipelineId,
  confidenceThreshold,
}: TriggerSyntheticDefectPipelineArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/synthetic_defect/${modelId}/trigger`,
      getOptions({
        signalId: 'postTriggerSyntheticDefectPipeline',
        data: {
          trainingPipelineId,
          confidenceThreshold,
        },
      }),
    )
    .json<TriggerSyntheticDefectPipelineResponse>();
};

/**
 * Get the latest synthetic defect pipeline for a model.
 * Returns null if none exists (204 response).
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getLatestSyntheticDefectPipeline = async ({projectId, modelId}: AutoMlModelArgs) => {
  const response = await ky.get(
    `${autoMLEndpoint}/${projectId}/synthetic_defect/${modelId}/latest`,
    getOptions({signalId: 'getLatestSyntheticDefectPipeline'}),
  );
  if (response.status === 204) {
    return null;
  }
  return response.json() as Promise<GetSyntheticDefectPipelineResponse>;
};

/**
 * Cancel an image selection curation pipeline.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postCancelImageSelectionPipeline = ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/image_selection/pipelines/${pipelineId}/cancel`,
      getOptions({signalId: 'postCancelImageSelectionPipeline'}),
    )
    .json<GetImageCurationPipelineResponse>();
};

/**
 * Cancel an image cleanup curation pipeline.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postCancelImageCleanupPipeline = ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/image_cleanup/pipelines/${pipelineId}/cancel`,
      getOptions({signalId: 'postCancelImageCleanupPipeline'}),
    )
    .json<GetImageCurationPipelineResponse>();
};

/**
 * Cancel a synthetic defect pipeline.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postCancelSyntheticDefectPipeline = ({projectId, pipelineId}: AutoMlPipelineArgs) => {
  return ky
    .post(
      `${autoMLEndpoint}/${projectId}/synthetic_defect/pipelines/${pipelineId}/cancel`,
      getOptions({signalId: 'postCancelSyntheticDefectPipeline'}),
    )
    .json<GetSyntheticDefectPipelineResponse>();
};

export const getTrainingPipelineConfig = async ({projectId, pipelineId}: {projectId: string; pipelineId: string}) => {
  const options = getOptions({signalId: `getTrainingPipelineConfig-${pipelineId}`});
  return ky
    .get(`${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/config`, {
      ...options,
      parseJson: sanitizeNonFiniteJson,
    })
    .json<AutoMLPipelineConfigResponse>();
};

/**
 * Fetch a paginated page of confusion-frontier image cards for the CM Image view.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getCmImageView = ({
  projectId,
  pipelineId,
  datasetId,
  interpretation,
  confidenceThreshold,
  areaSizeThreshold = 0,
  sortBy,
  anchor,
  offset,
  count,
  showMismatchesOnly,
  reactQuerySignal,
}: CMImageViewArgs & {reactQuerySignal?: AbortSignal}) => {
  const queryString = toQueryString({
    dataset_id: datasetId,
    interpretation,
    confidence_threshold: confidenceThreshold,
    area_size_threshold: areaSizeThreshold,
    sort_by: sortBy,
    anchor,
    offset,
    count,
    show_mismatches_only: showMismatchesOnly,
  });

  return ky
    .get(
      `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/cm_image_view?${queryString}`,
      getOptions({signalId: 'getCmImageView', reactQuerySignal}),
    )
    .json<CMImageViewResponse>();
};

/**
 * Fetch the lightweight full-set distribution used by the CM Image view bar.
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getCmImageViewDistribution = ({
  projectId,
  pipelineId,
  datasetId,
  interpretation,
  confidenceThreshold,
  areaSizeThreshold = 0,
  bins,
  showMismatchesOnly = false,
  reactQuerySignal,
}: CMImageDistributionArgs & {reactQuerySignal?: AbortSignal}) => {
  const queryString = toQueryString({
    dataset_id: datasetId,
    interpretation,
    confidence_threshold: confidenceThreshold,
    area_size_threshold: areaSizeThreshold,
    bins,
    show_mismatches_only: showMismatchesOnly,
  });

  return ky
    .get(
      `${autoMLEndpoint}/${projectId}/pipelines/${pipelineId}/cm_image_view/distribution?${queryString}`,
      getOptions({signalId: 'getCmImageViewDistribution', reactQuerySignal}),
    )
    .json<CMImageDistributionResponse>();
};

export const postExportEvaluationTestResults = async ({
  projectId,
  modelIds,
}: {
  projectId: string;
  modelIds: string[];
}) => {
  const options = getOptions({signalId: 'postExportEvaluationTestResults', data: {modelIds}});
  return ky.post(`${autoMLEndpoint}/${projectId}/models/test_results/export`, options).blob();
};
